import { useCallback, useState } from 'react';
import { CameraView, takeFiles } from './CameraView';

const buttonClass =
  'relative inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-content-secondary';
const enabledClass = 'cursor-pointer hover:bg-interactive-hover hover:text-content-primary';
const disabledClass = 'cursor-not-allowed opacity-30';

/**
 * Opens the in-app camera, which shows the item's photos and offers the gallery. `onDone` follows
 * the last `onFiles` of a visit: when the camera closes, or right after the phone camera's files.
 */
export function PhotoCaptureButton({
  itemId,
  previews,
  saved,
  error,
  onFiles,
  onDone,
  disabled = false,
}: {
  itemId: string;
  previews: Record<string, string>;
  saved?: string[];
  error?: string;
  onFiles: (files: File[]) => void;
  onDone?: () => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => {
    setOpen(false);
    onDone?.();
  }, [onDone]);
  const title = disabled ? 'Pick a group and category first' : 'Take photo';
  const className = `${buttonClass} ${disabled ? disabledClass : enabledClass}`;

  // getUserMedia needs a secure context. Without it, hand off to the phone's own camera.
  if (!navigator.mediaDevices?.getUserMedia) {
    return (
      <label title={title} className={className}>
        <CameraPlusIcon />
        <input
          type="file"
          accept="image/*"
          capture="environment"
          aria-label="Take photo"
          disabled={disabled}
          onChange={(e) => {
            onFiles(takeFiles(e));
            onDone?.();
          }}
          className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
      </label>
    );
  }
  return (
    <>
      <button
        type="button"
        title={title}
        aria-label="Take photo"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={className}
      >
        <CameraPlusIcon />
      </button>
      {open && (
        <CameraView
          itemId={itemId}
          previews={previews}
          saved={saved}
          error={error}
          onFiles={onFiles}
          onClose={close}
        />
      )}
    </>
  );
}

function CameraPlusIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <path d="M12 10v6M9 13h6" />
    </svg>
  );
}
